<template>
  <dashboard-layout title="Notifications">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search notifications…" />
    </template>

    <stat-grid>
      <stat-card label="Total notifications" :value="list.length.toLocaleString('en-US')" icon="bell" accent />
      <stat-card label="Distinct users" :value="distinctUsers.toLocaleString('en-US')" icon="users" />
    </stat-grid>

    <data-panel flush>
      <template #title>Notifications <span class="muted">({{ filtered.length.toLocaleString('en-US') }})</span></template>

      <loading-state v-if="loading" label="Loading notifications…" />
      <data-table
        v-else
        :columns="columns"
        :rows="filtered"
        row-key="id"
      >
        <template #cell-reason="{ row }">
          <span class="msg" :title="row.reason">{{ truncate(row.reason, 140) }}</span>
        </template>
        <template #cell-userId="{ row }">
          <router-link :to="`/user/${row.userId}`" class="user-link" @click.stop>{{ row.userId }}</router-link>
        </template>
        <template #cell-createdAt="{ row }">
          <span class="nowrap">{{ formatDate(row.createdAt) }}</span>
        </template>
        <template #empty>No notifications</template>
      </data-table>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebaseNotificationService } from '../services/firebaseNotificationService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'

const columns = [
  { key: 'reason', label: 'Reason' },
  { key: 'userId', label: 'User ID', width: '160px' },
  { key: 'createdAt', label: 'Date', width: '170px' }
]

export default {
  name: 'NotificationDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, LoadingState
  },
  setup() {
    const service = new FirebaseNotificationService()
    const list = ref([])
    const searchQuery = ref('')
    const loading = ref(true)

    const load = async () => {
      loading.value = true
      try {
        list.value = await service.getAllNotifications()
      } finally {
        loading.value = false
      }
    }

    const distinctUsers = computed(() => new Set(list.value.map(n => n.userId).filter(Boolean)).size)

    const filtered = computed(() => {
      const q = searchQuery.value.toLowerCase()
      return list.value.filter(n =>
        (n.reason || '').toLowerCase().includes(q) ||
        (n.userId || '').toLowerCase().includes(q)
      )
    })

    const truncate = (t, m) => !t ? '-' : t.length > m ? t.slice(0, m) + '...' : t
    const formatDate = ts => {
      if (!ts) return '-'
      if (ts.seconds) { const d = new Date(ts.seconds * 1000); return d.toLocaleString('tr-TR') }
      return new Date(ts).toLocaleString('tr-TR')
    }

    onMounted(load)

    return { list, searchQuery, loading, columns, distinctUsers, filtered, truncate, formatDate }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }
.msg {
  display: inline-block;
  max-width: 640px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.user-link { color: var(--primary-color); word-break: break-all; text-decoration: none; }
.user-link:hover { text-decoration: underline; }
</style>
